'use client';

import { getThemeTextClass } from '@/lib/theme';
import Image from 'next/image';
import { TCustomIcon, THeroIcon } from '../types';
import { ITheme } from '../types/theme';
import Link from 'next/link';
import Icon from './Icon';

interface BusinessTypeCardProps {
  title: string;
  description: string;
  imageIcon?: string;
  icon?: THeroIcon | TCustomIcon;
  theme: ITheme;
  category?: string;
  showLearnMore?: boolean;
  learnMoreText?: string;
  showChallengeLabel?: boolean;
  showRecommendedLabel?: boolean;
  className?: string;
  href?: string;
}

const BusinessTypeCard: React.FC<BusinessTypeCardProps> = ({
  title,
  description,
  imageIcon,
  icon,
  theme,
  category,
  showLearnMore = false,
  learnMoreText,
  showChallengeLabel = false,
  showRecommendedLabel = false,
  className = '',
  href,
}) => {
  // Get theme-based Tailwind classes
  const getThemeClasses = (theme: ITheme) => {
    const themeMap = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
      },
      pink: {
        bg: 'bg-pink-100',
        border: 'border-pink-200',
      },
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
      },
      red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
      },
      teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
      },
      cyan: {
        bg: 'bg-cyan-50',
        border: 'border-cyan-200',
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
      },
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
      },
    };
    return themeMap[theme as keyof typeof themeMap] || themeMap.purple;
  };

  const themeClasses = getThemeClasses(theme);
  const textColorClass = getThemeTextClass(theme);

  const cardContent = (
    <div className={`overflow-hidden rounded-xl ${className}`}>
      {/* Themed Header with Icon */}
      <div
        className={`relative h-48 rounded-t-xl overflow-hidden flex items-center justify-center dark:!bg-gray-800 ${showRecommendedLabel ? 'bg-white' : themeClasses.bg}`}
      >
        {/* Decorative bubbles or circle */}
        {showRecommendedLabel ? (
          <div
            className={`absolute -top-12 -right-12 w-32 h-32 rounded-full dark:!bg-gray-700 ${themeClasses.bg}`}
          ></div>
        ) : (
          <>
            <div className="absolute top-4 right-8 w-16 h-16 bg-white/50 dark:bg-gray-800/50 rounded-full blur-xl"></div>
            <div className="absolute bottom-8 left-8 w-24 h-24 bg-white/50 dark:bg-gray-800/50 rounded-full blur-xl"></div>
            <div className="absolute top-1/2 right-12 w-12 h-12 bg-white/50 dark:bg-gray-800/50 rounded-full blur-lg"></div>
          </>
        )}

        {/* Icon */}
        {(imageIcon || icon) && (
          <div
            className={`relative w-32 h-32 z-10 rounded-full bg-white flex items-center justify-center shadow-lg border ${themeClasses.border} dark:!border-gray-600 ${icon ? 'dark:bg-gray-800' : ''}`}
          >
            {imageIcon ? (
              <div className="relative w-20 h-20">
                <Image src={imageIcon} alt="" fill className="object-contain" />
              </div>
            ) : icon ? (
              <Icon
                icon={icon}
                className="w-16 h-16 text-gray-900 dark:text-white"
              />
            ) : null}
          </div>
        )}

        {/* Category Badge */}
        {category && (
          <div className="absolute top-4 left-4">
            <span className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm text-gray-800 dark:text-gray-200 px-3 py-1 text-xs font-semibold rounded shadow-sm">
              {category}
            </span>
          </div>
        )}

        {/* Challenge Badge */}
        {showChallengeLabel && (
          <div className="absolute top-4 left-4">
            <span className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm text-gray-800 dark:text-gray-200 px-3 py-1 text-xs font-semibold rounded shadow-sm inline-flex items-center gap-1">
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              Challenge
            </span>
          </div>
        )}

        {/* Recommended Badge */}
        {showRecommendedLabel && (
          <div className="absolute top-4 left-4">
            <span className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm text-gray-800 dark:text-gray-200 px-3 py-1 text-xs font-semibold rounded shadow-sm inline-flex items-center gap-1 border border-gray-300 dark:border-gray-600">
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Recommended
            </span>
          </div>
        )}
      </div>

      {/* Card Content */}
      <div className="p-4 flex flex-col" style={{ minHeight: '200px' }}>
        <h3
          className={`text-lg font-semibold text-gray-900 dark:text-white mb-2 ${showChallengeLabel ? '' : 'line-clamp-2 min-h-[3.5rem]'}`}
        >
          {title}
        </h3>

        <p
          className={`text-gray-600 dark:text-gray-300 text-xs mb-4 flex-1 ${showChallengeLabel ? '' : 'line-clamp-2'}`}
        >
          {description}
        </p>

        {showLearnMore && learnMoreText && (
          <div
            className={`inline-flex items-center font-medium text-sm ${textColorClass}`}
          >
            {learnMoreText}
            <svg
              className="w-4 h-4 ml-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        )}
      </div>
    </div>
  );

  // Wrap with Link if href is provided, otherwise wrap with div
  if (href) {
    return (
      <Link
        href={href}
        className="card overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block"
        style={{ padding: '0' }}
      >
        {cardContent}
      </Link>
    );
  }

  // If no href but still want card styling (for non-clickable cards in pain points section)
  return (
    <div className="card overflow-hidden block" style={{ padding: '0' }}>
      {cardContent}
    </div>
  );
};

export default BusinessTypeCard;
